import { newUUID } from '@/utils/uuid';
import { useState, useCallback, useEffect, useRef } from 'react';

interface SidePanelState<T> {
  content: T;
  key: string;
}

interface UseSidePanelHandlerReturn<T> {
  content: T | null;
  panelKey: string | null;
  isOpen: boolean;
  open: (content: T) => void;
  close: () => void;
}

export function useSidePanelHandler<T extends { type: string }>(
  clearDelay = 300
): UseSidePanelHandlerReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<SidePanelState<T> | null>(null);
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current);
      setState(null);
    };
  }, []);

  const open = useCallback((newContent: T) => {
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setState({ content: newContent, key: newUUID() });
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    clearTimeoutRef.current = setTimeout(() => {
      setState(null);
      clearTimeoutRef.current = null;
    }, clearDelay);
  }, [clearDelay]);

  return {
    content: state?.content ?? null,
    panelKey: state?.key ?? null,
    isOpen,
    open,
    close,
  };
}
